/**
 * The Realm's Chronicle — the shape, and the small levers the deck is built out
 * of.
 *
 * The settlement has had a chronicle since early on: random events with real
 * choices, drawn at the end of a siege, freezing the world until they are
 * answered. It is good, and it fires only inside one town. A campaign of a
 * hundred and ninety-one provinces, thirty crowns, fifteen simultaneous wars,
 * twenty-four trade nodes and nine bandit camps generated **nothing at all**.
 *
 * This is the same idea on the strategic board, and it deliberately copies the
 * settlement's shape rather than inventing a second one. Everything that made
 * `src/data/events.ts` work is kept:
 *
 *   1. **Two or three choices, and they must actually differ.** Each choice
 *      moves a *different* thing: coin against standing, ground against faith,
 *      a certain small loss against an uncertain large one.
 *   2. **There is always a way out that costs nothing but pride.** Every event
 *      carries at least one choice with no `cost`, so a broke crown can never be
 *      deadlocked in front of a question it cannot pay for. There is a test.
 *   3. **`rules` is the exact mechanical text.** Flavour may be evasive; the
 *      rules line may not.
 *   4. **Nothing draws from `Math.random`.** Every roll is `w.rngCampaign`,
 *      whose cursor is hashed and saved.
 *
 * And four things are new, because the strategic board is not a town:
 *
 *   5. **An event fires off the board, not off a die.** `when` is handed the
 *      campaign and a crown and returns a `RealmEventCtx` — *which* province,
 *      *which* rival, *which* host, *which* region — or `null`. The context is
 *      the reason it fired, it is frozen into the pending record, and the
 *      flavour text is built from it. An event that reads the board is worth ten
 *      that read a die.
 *   6. **Every crown answers, not just the player.** Each choice carries an
 *      `ai` weight, so the thirty other crowns are playing the same game rather
 *      than living in a realm where nothing ever happens to them. A player's
 *      event suspends the realm; a crown's is answered on the spot in council.
 *   7. **Costs are gold and men, and nothing else.** Materials live in city
 *      yards rather than in the treasury — `doIncome` deposits them there — so a
 *      cost in timber would be unpayable for reasons that have nothing to do
 *      with whether the crown is rich. See `payable`.
 *   8. **Effects are on state that already exists.** Unrest, garrisons, opinion,
 *      gold, manpower, camps, walls, hosts — and the four estates, which are the
 *      one durable channel an answer can write to. No event invents a modifier.
 */

import type { Rng } from '../../core/rng.ts';
import { clamp } from '../../core/math.ts';
import type { CampaignState } from './types.ts';

/**
 * What an event is *about*.
 *
 * Every slot is optional and defaults to -1. The context is computed once, by
 * `when`, at draw time, and then frozen — so the succession that fired because
 * of a particular province is answered about that province even if the board has
 * moved on by the time the player clicks. This is the same argument that freezes
 * the offered choice ids: the answer has to mean what the player read.
 */
export interface RealmEventCtx {
  /** The crown this is happening to. Always set. */
  nation: number;
  /** A province it is about, or -1. */
  province: number;
  /** Another crown it is about, or -1. */
  other: number;
  /** An army id it is about, or -1. */
  army: number;
  /** A region index it is about, or -1. */
  region: number;
}

export function ctx(
  nation: number,
  parts: Partial<Omit<RealmEventCtx, 'nation'>> = {},
): RealmEventCtx {
  return {
    nation,
    province: parts.province ?? -1,
    other: parts.other ?? -1,
    army: parts.army ?? -1,
    region: parts.region ?? -1,
  };
}

/**
 * What an answer may be charged.
 *
 * Gold and men, and nothing else, for the reason in the header: the treasury
 * holds coin and the *cities* hold materials, so a cost in stone would refuse a
 * rich crown whose timber happened to be four weeks away. Both are national and
 * both are already hashed and saved.
 */
export interface RealmCost {
  gold?: number;
  manpower?: number;
}

export interface RealmChoice {
  /** Stable across saves — the resolution is recorded as `nation:event>choice`. */
  id: string;
  /** What the player clicks. Imperative, short. */
  label: string;
  /**
   * Exact mechanics, built from the context so the numbers on the card are the
   * numbers that will happen. A function rather than a string because "unrest
   * across your four holdings" wants to say *four*.
   */
  rules: (c: CampaignState, k: RealmEventCtx) => string;
  /**
   * Charged before `apply` runs. At least one choice per event must be free —
   * see the deck test.
   *
   * A function of the context because the price of a succession should scale
   * with what is being inherited.
   */
  cost?: (c: CampaignState, k: RealmEventCtx) => RealmCost;
  /**
   * How much a crown wants this, given its temperament and the board. Higher
   * wins; ties break on declaration order, which is stable.
   *
   * This is the whole of the AI, and it lives on the choice rather than in
   * `ai.ts` on purpose: the crown that has to decide whether to hang the envoy
   * is deciding about *this envoy*, and the only place that knows what hanging
   * him does is the card that says so. `ai.ts` needs no change at all.
   */
  ai: (c: CampaignState, k: RealmEventCtx) => number;
  /** Mutates the campaign and returns the line for the chronicle log. */
  apply: (c: CampaignState, k: RealmEventCtx, rng: Rng) => string;
}

/**
 * What sort of thing this is. Not a mechanic — a shape, so the deck can be read
 * at a glance and a run can be checked for having more than one flavour of bad
 * news in it.
 */
export type RealmEventKind = 'crown' | 'estate' | 'war' | 'country' | 'trade';

export interface RealmEventDef {
  id: string;
  title: string;
  kind: RealmEventKind;
  /** One line for the chronicle log, before the question is put. */
  summary: string;
  /**
   * The paragraph, built from the context.
   *
   * A function, and this is the rule the settlement deck could do without and
   * this one cannot: **the reason it fired is stated in its text.** A bad
   * harvest names the region it is in and what that ground is like; a general
   * who will not march names the host and how long it has stood. An event that
   * fired off the board and then described itself in generalities is
   * indistinguishable from one that fired off a die.
   */
  flavour: (c: CampaignState, k: RealmEventCtx) => string;
  /** Relative draw weight among everything currently eligible. */
  weight: number;
  /**
   * How many times this may fire for one crown within living memory. One by
   * default.
   *
   * "Within living memory" is exact rather than evasive: the history is bounded
   * at `MAX_EVENT_HISTORY`, so on a campaign long enough to overflow it the
   * oldest answers roll off and a once-only card may be offered again. That is a
   * defensible reading — it has been forty years — and it is the price of not
   * carrying an unbounded array in the hash of every save.
   */
  repeat?: number;
  /**
   * The gate, and the subject-picker in one.
   *
   * Returns the thing the event is about, or `null` when the board is not
   * currently producing this event for this crown. It must be **pure and
   * deterministic**: no random numbers, and any choice among several candidates
   * settled by index order or by a score, never by iteration luck.
   */
  when: (c: CampaignState, nation: number) => RealmEventCtx | null;
  /** Whether the frozen subject still belongs to this decision. Unlike `when`,
   * this must not choose a different province, army or rival on resolution. */
  validContext?: (c: CampaignState, k: RealmEventCtx) => boolean;
  choices: RealmChoice[];
}

/**
 * A question on a crown's table.
 *
 * Only ever the player's. An AI crown's event is answered inside the same
 * council that drew it and never becomes state — there is nothing to suspend,
 * because nobody is waiting to be asked.
 */
export interface PendingRealmEvent {
  /** Id into `REALM_EVENTS`. */
  id: string;
  /**
   * The choice ids actually offered, frozen at draw time. The offer must not be
   * recomputed at resolve time — the answer has to mean what the player read.
   */
  choices: string[];
  /** What it is about, frozen for the same reason. */
  ctx: RealmEventCtx;
  /** The turn it was drawn on. Part of the modal's identity for the UI. */
  turn: number;
}

// ── Cadence ────────────────────────────────────────────────────────────────
//
// Every number below was set against a measured board rather than guessed. See
// the fire-rate table in COORDINATION.md.

/**
 * Months between a crown's draws.
 *
 * Six, staggered by nation index, so five of the thirty crowns draw in any given
 * month and the work is spread rather than landing in one lump every half year.
 * The player therefore *sees* a question at most twice a year and, after the
 * roll below, rather less often than that.
 */
export const EVENT_PERIOD = 6;

/**
 * Odds a crown's draw month actually produces one.
 *
 * Together with `EVENT_PERIOD` this puts a question in front of the player about
 * once every eleven months — roughly thirteen over a long campaign, which is a
 * chronicle rather than a notification stream. It is also low enough that a
 * crown with only one eligible card does not get that card on a metronome.
 */
export const EVENT_CHANCE = 0.55;

/** Nothing fires before this. The opening board has no history to be about. */
export const EVENT_FIRST_TURN = 8;

/**
 * Answers kept, oldest dropped first.
 *
 * Three hundred and twenty is about eleven per crown, which covers a two-hundred
 * month campaign at the cadence above. It is bounded because it is hashed and
 * saved, and an unbounded array in a save that runs for tens of hours is how a
 * save file becomes the largest thing in the project.
 */
export const MAX_EVENT_HISTORY = 320;

// ── The small levers the deck is written in ────────────────────────────────

/** The key one answer is recorded under. Crown-scoped, so two crowns do not share a memory. */
export function historyKey(nation: number, eventId: string, choiceId: string): string {
  return `${nation}:${eventId}>${choiceId}`;
}

/** Subject metadata shares the existing bounded, saved and hashed history.
 * The @ prefix keeps it separate from legacy answer keys and repeat counts. */
export function subjectHistoryKey(nation: number, eventId: string, choiceId: string, other: number, turn: number): string {
  return `@${historyKey(nation, eventId, choiceId)}|${other}|${turn}`;
}

/** Newest matching subjects first. Old saves simply have no subject markers. */
export function eventSubjects(c: CampaignState, nation: number, eventId: string, choiceId?: string): Array<{ other: number; turn: number }> {
  const prefix = `@${nation}:${eventId}>`, out: Array<{ other: number; turn: number }> = [];
  for (let i = c.eventHistory.length - 1; i >= 0; i--) {
    const entry = c.eventHistory[i];
    if (!entry.startsWith(prefix)) continue;
    const [choice, otherText, turnText] = entry.slice(prefix.length).split('|');
    if (choiceId !== undefined && choice !== choiceId) continue;
    const other = Number(otherText), turn = Number(turnText);
    if (Number.isInteger(other) && other >= 0 && Number.isInteger(turn) && turn >= 0) out.push({ other, turn });
  }
  return out;
}

/** How many times this crown has answered this card, within living memory. */
export function timesFiredBy(c: CampaignState, nation: number, eventId: string): number {
  const prefix = `${nation}:${eventId}>`;
  let n = 0;
  for (const h of c.eventHistory) if (h.startsWith(prefix)) n++;
  return n;
}

/** Did this crown answer this card *this* way? The chain rule. */
export function answeredBy(
  c: CampaignState,
  nation: number,
  eventId: string,
  choiceId: string,
): boolean {
  return c.eventHistory.includes(historyKey(nation, eventId, choiceId));
}

/** Coin into a crown's purse. Never negative. */
export function giveGold(c: CampaignState, nation: number, amount: number): number {
  const n = c.nations[nation];
  if (!n) return 0;
  const got = Math.max(0, Math.round(amount));
  n.treasury.gold += got;
  return got;
}

/** Coin out of it, reporting what was actually there. */
export function takeGold(c: CampaignState, nation: number, amount: number): number {
  const n = c.nations[nation];
  if (!n) return 0;
  const got = Math.min(n.treasury.gold, Math.max(0, Math.round(amount)));
  n.treasury.gold -= got;
  return got;
}

export function giveManpower(c: CampaignState, nation: number, amount: number): number {
  const n = c.nations[nation];
  if (!n) return 0;
  const got = Math.max(0, Math.round(amount));
  n.manpower += got;
  return got;
}

export function takeManpower(c: CampaignState, nation: number, amount: number): number {
  const n = c.nations[nation];
  if (!n) return 0;
  const got = Math.min(n.manpower, Math.max(0, Math.round(amount)));
  n.manpower -= got;
  return got;
}

/**
 * Unrest onto a list of provinces, clamped, reporting how many were touched.
 *
 * Unrest is the strategic layer's morale: it is already hashed, already saved,
 * already decays on its own schedule and already scales what a province pays and
 * how fast its garrison refills. Every event that wants to make a crown's life
 * harder without taking anything off the map reaches for this.
 */
export function addUnrest(c: CampaignState, provinces: number[], delta: number): number {
  let n = 0;
  for (const p of provinces) {
    const prov = c.provinces[p];
    if (!prov) continue;
    prov.unrest = clamp(prov.unrest + delta, 0, 1);
    n++;
  }
  return n;
}

/** Every province a crown holds, in index order so a replay touches the same ground. */
export function heldBy(c: CampaignState, nation: number): number[] {
  const out: number[] = [];
  for (let p = 0; p < c.provinces.length; p++) if (c.provinces[p].owner === nation) out.push(p);
  return out;
}

/** Can this crown pay? The gate on offering a choice at all. */
export function payable(c: CampaignState, nation: number, cost: RealmCost | undefined): boolean {
  if (!cost) return true;
  const n = c.nations[nation];
  if (!n) return false;
  if ((cost.gold ?? 0) > n.treasury.gold) return false;
  if ((cost.manpower ?? 0) > n.manpower) return false;
  return true;
}

/** Charge it. Called only after `payable` has said yes. */
export function charge(c: CampaignState, nation: number, cost: RealmCost | undefined): void {
  if (!cost) return;
  if (cost.gold) takeGold(c, nation, cost.gold);
  if (cost.manpower) takeManpower(c, nation, cost.manpower);
}

/** The price, as a sentence, for the card's corner. */
export function costText(cost: RealmCost | undefined): string {
  if (!cost) return '';
  const parts: string[] = [];
  if (cost.gold) parts.push(`${Math.round(cost.gold)} gold`);
  if (cost.manpower) parts.push(`${Math.round(cost.manpower)} men`);
  return parts.join(', ');
}

/** `n thing` / `n things`, because half the rules lines want it. */
export function plural(n: number, one: string, many?: string): string {
  return `${n} ${n === 1 ? one : (many ?? `${one}s`)}`;
}
